import {
  type BeneficiaryKind,
  type BookkeepingLine,
  type BookkeepingReference,
  DUE_ON_SOURCE_LABELS,
  EXPENSE_CLASSIFICATION_LABELS,
  EXPENSE_CLASSIFICATIONS,
  type ExpenseClassification,
  formatMinor,
  SETTLEMENT_LABELS,
  SETTLEMENTS,
  type Settlement,
  TRANSACTION_STATUS_LABELS,
  type UpdateRecordRequest,
} from '@hvac/contracts';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Field,
  Input,
  Skeleton,
} from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { sessionQuery } from '../../shared/api/queries.js';
import {
  bookkeepingKeys,
  bookkeepingRecordQuery,
  bookkeepingReferenceQuery,
  bookkeepingSettingsQuery,
  confirmBookkeepingRecord,
  correctBookkeepingRecord,
  sendRecordBackToReview,
  updateBookkeepingRecord,
  voidBookkeepingRecord,
} from '../../shared/api/bookkeeping-queries.js';
import { ObjectTimeline, RelationshipsPanel } from '../objects/object-panels.js';
import { InsightsPanel } from './insights-panel.js';
import { WhoBenefited } from './who-benefited.js';
import { TaxTreatmentPanel } from '../tax/tax-treatment-panel.js';
import { LastTimeCard, pairLinesWithTemplate } from './last-time-card.js';

/**
 * The record editor.
 *
 * What a bookkeeper actually decides: which supplier, which account, whether
 * it is an expense or a capital asset, and which job it belongs to. Amounts
 * are shown but never editable here, they came from a document a human
 * already confirmed, and a screen that lets someone quietly retype a total is
 * a screen where the books stop agreeing with the paper.
 *
 * Confirming does not export anything. QuickBooks remains the system of
 * record and nothing on this page writes to it.
 */

interface Draft {
  vendorId: string | null;
  reference: string;
  spentByTechnicianId: string | null;
  settlement: Settlement;
  dueOn: string;
  lines: Record<
    string,
    {
      categoryId: string;
      classification: ExpenseClassification;
      beneficiary: BeneficiaryKind;
      divisionId: string | null;
      ccaClassId: string | null;
      jobId: string | null;
      vehicleId: string | null;
    }
  >;
}

/** An ISO date plus some days, computed in UTC so no time zone moves the day. */
function addDays(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function BookkeepingRecordPage() {
  const { recordId } = useParams({ from: '/protected/bookkeeping/$recordId' });
  const navigate = useNavigate();
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const queryClient = useQueryClient();

  const record = useQuery(bookkeepingRecordQuery(organizationId, recordId));
  const reference = useQuery(bookkeepingReferenceQuery(organizationId));
  const settings = useQuery(bookkeepingSettingsQuery(organizationId));

  const [draft, setDraft] = useState<Draft | null>(null);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: bookkeepingKeys.all(organizationId) });
  };

  const save = useMutation({
    mutationFn: (body: UpdateRecordRequest) =>
      updateBookkeepingRecord(organizationId, recordId, body),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      setDraft(null);
      setReason('');
      invalidate();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'That change could not be saved.'),
  });

  const confirm = useMutation({
    mutationFn: () =>
      confirmBookkeepingRecord(
        organizationId,
        recordId,
        notes.trim() === '' ? {} : { notes: notes.trim() },
      ),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    // The database refuses a record that still needs a decision and names the
    // one it is missing. Shown as written rather than replaced by "failed".
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'That record could not be confirmed.'),
  });

  // A signed record is never edited: it is voided, or superseded by a draft
  // copy. Both need a reason, which stays on the record and in the Timeline.
  const [whyGone, setWhyGone] = useState('');
  const voidRecord = useMutation({
    mutationFn: () => voidBookkeepingRecord(organizationId, recordId, whyGone.trim()),
    onSuccess: () => {
      setError(null);
      setWhyGone('');
      invalidate();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'That record could not be voided.'),
  });
  const correctRecord = useMutation({
    mutationFn: () => correctBookkeepingRecord(organizationId, recordId, whyGone.trim()),
    onSuccess: (result) => {
      setError(null);
      setWhyGone('');
      invalidate();
      void navigate({ to: '/bookkeeping/$recordId', params: { recordId: result.recordId } });
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'That record could not be corrected.'),
  });

  // Cmd/Ctrl+S saves the changes; Cmd/Ctrl+Enter confirms a record with none
  // pending. Both work from inside a field, which is where the hands are.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.key === 's' && draft !== null) {
        event.preventDefault();
        document.getElementById('record-save')?.click();
      } else if (event.key === 'Enter' && draft === null && record.data?.status === 'draft') {
        event.preventDefault();
        confirm.mutate();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (record.isPending || reference.isPending) {
    return (
      <div className="flex flex-col gap-3" aria-label="Loading">
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-4 w-80" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (record.isError) {
    return (
      <Alert tone="danger" title="That record could not be loaded">
        {record.error instanceof Error ? record.error.message : 'Unknown error.'}
      </Alert>
    );
  }

  const data = record.data;
  const lists: BookkeepingReference = reference.data ?? {
    vendors: [],
    categories: [],
    jobs: [],
    divisions: [],
    employees: [],
    vehicles: [],
    ccaClasses: [],
  };
  // A shop that runs one trade has no divisions and is never shown the idea.
  const hasDivisions = lists.divisions.length > 0;
  const editable = data.status === 'draft';

  const lineDraft = (line: BookkeepingLine) =>
    draft?.lines[line.id] ?? {
      categoryId: line.categoryId,
      classification: line.classification,
      beneficiary: line.beneficiary,
      divisionId: line.divisionId,
      ccaClassId: line.ccaClassId,
      jobId: line.jobId,
      vehicleId: line.vehicleId,
    };

  const current: Draft = draft ?? {
    vendorId: data.vendorId,
    reference: data.reference ?? '',
    spentByTechnicianId: data.spentByTechnicianId,
    settlement: data.settlement,
    dueOn: data.dueOn ?? '',
    lines: {},
  };

  const patch = (next: Partial<Draft>) => {
    setSaved(false);
    setDraft({ ...current, ...next });
  };

  const patchLine = (line: BookkeepingLine, next: Partial<Draft['lines'][string]>) => {
    setSaved(false);
    setDraft({
      ...current,
      lines: { ...current.lines, [line.id]: { ...lineDraft(line), ...next } },
    });
  };

  const onSave = () => {
    if (draft === null) return;
    const body: UpdateRecordRequest = {
      vendorId: draft.vendorId,
      reference: draft.reference.trim() === '' ? null : draft.reference.trim(),
      spentByTechnicianId: draft.spentByTechnicianId,
      settlement: draft.settlement,
      ...(draft.settlement === 'on_account' && draft.dueOn !== '' && draft.dueOn !== data.dueOn
        ? { dueOn: draft.dueOn }
        : {}),
      lines: Object.entries(draft.lines).map(([id, values]) => ({
        id,
        categoryId: values.categoryId,
        classification: values.classification,
        beneficiary: values.beneficiary,
        divisionId: values.divisionId,
        ccaClassId: values.ccaClassId,
        jobId: values.jobId,
        vehicleId: values.vehicleId,
      })),
      ...(reason.trim().length >= 3 ? { reason: reason.trim() } : {}),
    };
    save.mutate(body);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/bookkeeping" className="text-sm text-content-muted hover:text-content">
            ← Bookkeeping
          </Link>
          <h1 className="mt-1 text-xl font-semibold text-content">
            {data.vendorId === null ? (
              'Unmatched supplier'
            ) : (
              <Link
                to="/suppliers/$vendorId"
                params={{ vendorId: data.vendorId }}
                className="hover:underline"
              >
                {data.vendorName ?? 'Supplier'}
              </Link>
            )}
          </h1>
          <p className="text-sm text-content-muted">
            {data.transactionDate} · {TRANSACTION_STATUS_LABELS[data.status]} ·{' '}
            {formatMinor(data.grossAmountMinor, data.currencyCode)}
          </p>
        </div>
        {data.sourceDocumentId === null ? null : (
          <Link
            to="/documents/$documentId"
            params={{ documentId: data.sourceDocumentId }}
            className="text-sm text-brand-600 hover:underline"
          >
            {data.documentFilename ?? 'Source document'}
          </Link>
        )}
      </div>

      {error !== null ? <Alert tone="danger">{error}</Alert> : null}
      {saved ? <Alert tone="success">Saved. Nothing has been exported.</Alert> : null}
      {data.status === 'voided' ? (
        <Alert tone="warning" title="This record was voided">
          {data.voidReason ?? 'No reason was recorded.'}
          {data.voidedAt === null ? '' : ` (${data.voidedAt.slice(0, 10)})`}. Its postings were
          reversed, not removed, and any bank line it explained is waiting to be explained again.
        </Alert>
      ) : null}
      {data.status === 'superseded' && data.supersededByRecordId !== null ? (
        <Alert tone="warning" title="This record was replaced by a corrected copy">
          {data.voidReason ?? ''}{' '}
          <Link
            to="/bookkeeping/$recordId"
            params={{ recordId: data.supersededByRecordId }}
            className="underline"
          >
            Open the copy that stands in its place.
          </Link>
        </Alert>
      ) : null}
      {data.correctsRecordId === null ? null : (
        <Alert tone="info" title="This record corrects an earlier one">
          It began as a copy of a signed record whose postings were reversed when this copy was
          made.{' '}
          <Link
            to="/bookkeeping/$recordId"
            params={{ recordId: data.correctsRecordId }}
            className="underline"
          >
            See the original.
          </Link>
        </Alert>
      )}
      {data.openReasons.length === 0 ? null : (
        <Alert tone="warning" title="This record still needs a decision">
          <ul className="list-inside list-disc">
            {data.openReasons.map((item) => (
              <li key={item}>{item.replaceAll('_', ' ')}</li>
            ))}
          </ul>
        </Alert>
      )}

      {editable && data.sourceDocumentId !== null ? (
        <SendBackToReview organizationId={organizationId} recordId={data.id} />
      ) : null}

      <Card>
        <CardHeader
          title="Header"
          description={
            editable && data.sourceDocumentId !== null
              ? "The amounts are the document's, as approved in Review. If one was misread, send it back to Review above to correct it."
              : 'The amounts came from the approved document and are not editable here.'
          }
        />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <Field label="Supplier" required={settings.data?.requireVendor ?? true}>
            {({ inputProps }) => (
              <select
                {...inputProps}
                value={current.vendorId ?? ''}
                disabled={!editable}
                onChange={(event) =>
                  patch({ vendorId: event.target.value === '' ? null : event.target.value })
                }
                className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
              >
                <option value="">Not matched</option>
                {lists.vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.displayName}
                  </option>
                ))}
              </select>
            )}
          </Field>

          {lists.employees.length === 0 ? null : (
            <Field
              label="Who spent it"
              hint="For a field purchase. Not the person who uploaded the paper."
            >
              {({ inputProps }) => (
                <select
                  {...inputProps}
                  value={current.spentByTechnicianId ?? ''}
                  disabled={!editable}
                  onChange={(event) =>
                    patch({
                      spentByTechnicianId: event.target.value === '' ? null : event.target.value,
                    })
                  }
                  className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
                >
                  <option value="">Not recorded</option>
                  {lists.employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.fullName}
                      {employee.employeeNumber === null ? '' : ` (${employee.employeeNumber})`}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}

          <Field label="Reference" hint="Invoice or receipt number.">
            {({ inputProps }) => (
              <Input
                {...inputProps}
                value={current.reference}
                disabled={!editable}
                onChange={(event) => patch({ reference: event.target.value })}
              />
            )}
          </Field>

          {data.transactionType !== 'expense' ? null : (
            <Field
              label="Paid?"
              hint={
                current.settlement === 'on_account'
                  ? data.settledMinor === '0'
                    ? 'Owed to the supplier until a bank line on the Bank page is matched to this record.'
                    : `${formatMinor(data.settledMinor, data.currencyCode)} has been matched from the bank so far.`
                  : 'Read from the payment block on the document. A supplier invoice with no payment shown is owed.'
              }
            >
              {({ inputProps }) => (
                <select
                  {...inputProps}
                  value={current.settlement}
                  disabled={!editable}
                  onChange={(event) => {
                    const settlement = event.target.value as Settlement;
                    patch({
                      settlement,
                      dueOn:
                        settlement === 'on_account' && current.dueOn === ''
                          ? addDays(data.transactionDate, 30)
                          : current.dueOn,
                    });
                  }}
                  className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
                >
                  {SETTLEMENTS.map((option) => (
                    <option key={option} value={option}>
                      {SETTLEMENT_LABELS[option]}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}

          {data.transactionType !== 'expense' || current.settlement !== 'on_account' ? null : (
            <Field
              label="Due on"
              hint={
                data.dueOnSource === null || current.dueOn !== (data.dueOn ?? '')
                  ? 'When the supplier expects payment.'
                  : `Due date ${DUE_ON_SOURCE_LABELS[data.dueOnSource]}.`
              }
            >
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  type="date"
                  value={current.dueOn}
                  min={data.transactionDate}
                  disabled={!editable}
                  onChange={(event) => patch({ dueOn: event.target.value })}
                />
              )}
            </Field>
          )}

          <div className="sm:col-span-2 grid grid-cols-3 gap-4 border-t border-border pt-3 text-sm">
            <Amount label="Net" value={data.netAmountMinor} currency={data.currencyCode} />
            <Amount label="HST" value={data.taxAmountMinor} currency={data.currencyCode} />
            <Amount label="Total" value={data.grossAmountMinor} currency={data.currencyCode} />
          </div>
        </CardBody>
      </Card>

      {editable && data.vendorId !== null ? (
        <LastTimeCard
          organizationId={organizationId}
          recordId={recordId}
          onApply={(template) => {
            const pairs = pairLinesWithTemplate(data.lines, template.lines);
            setSaved(false);
            setDraft({
              ...current,
              ...(template.settlement === 'paid' || template.settlement === 'on_account'
                ? { settlement: template.settlement }
                : {}),
              lines: Object.fromEntries(
                pairs.map(([line, source]) => [
                  line.id,
                  {
                    ...lineDraft(line),
                    categoryId: source.categoryId,
                    classification: source.classification,
                    beneficiary: source.beneficiary,
                    divisionId: source.divisionId,
                    ccaClassId: source.ccaClassId,
                    vehicleId: source.vehicleId ?? lineDraft(line).vehicleId,
                  },
                ]),
              ),
            });
          }}
        />
      ) : null}

      <InsightsPanel
        organizationId={organizationId}
        recordId={recordId}
        editable={editable}
        categoryLabelFor={(categoryId) =>
          lists.categories.find((category) => category.id === categoryId)?.name ?? null
        }
        onApplyCategory={(categoryId) => {
          const first = data.lines[0];
          if (first !== undefined) patchLine(first, { categoryId });
        }}
        onApplyJob={(jobId) => {
          const first = data.lines[0];
          if (first !== undefined) patchLine(first, { jobId });
        }}
      />

      <TaxTreatmentPanel organizationId={organizationId} recordId={recordId} />

      <Card>
        <CardHeader title="Lines" description="Where the money goes on the books." />
        <CardBody className="flex flex-col gap-5">
          {data.lines.map((line) => {
            const values = lineDraft(line);
            return (
              <div
                key={line.id}
                className="grid gap-4 border-b border-border pb-5 last:border-0 sm:grid-cols-2"
              >
                <p className="sm:col-span-2 text-sm text-content-muted">
                  Line {line.lineNumber} · {line.description ?? 'No description'} ·{' '}
                  <span className="tabular">
                    {formatMinor(line.grossAmountMinor, data.currencyCode)}
                  </span>
                </p>

                <WhoBenefited
                  value={values.beneficiary}
                  disabled={!editable}
                  reason={line.beneficiaryReason}
                  onChange={(beneficiary) => patchLine(line, { beneficiary })}
                />

                <Field label="Category" required>
                  {({ inputProps }) => (
                    <select
                      {...inputProps}
                      value={values.categoryId}
                      disabled={!editable}
                      onChange={(event) => patchLine(line, { categoryId: event.target.value })}
                      className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
                    >
                      {lists.categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.code === null
                            ? category.name
                            : `${category.code}, ${category.name}`}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>

                <Field
                  label="Treatment"
                  {...(values.beneficiary === 'owner'
                    ? { hint: 'Decided by who benefited. A purchase for the owner is a draw.' }
                    : {})}
                >
                  {({ inputProps }) => (
                    <select
                      {...inputProps}
                      value={values.beneficiary === 'owner' ? 'owner_draw' : values.classification}
                      disabled={!editable || values.beneficiary === 'owner'}
                      onChange={(event) =>
                        patchLine(line, {
                          classification: event.target.value as ExpenseClassification,
                        })
                      }
                      className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
                    >
                      {EXPENSE_CLASSIFICATIONS.map((value) => (
                        <option key={value} value={value}>
                          {EXPENSE_CLASSIFICATION_LABELS[value]}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>

                {values.classification === 'capital_asset' ? (
                  <Field
                    label="CCA class"
                    required
                    hint="A capital asset cannot be confirmed without one."
                  >
                    {({ inputProps }) => (
                      <select
                        {...inputProps}
                        value={values.ccaClassId ?? ''}
                        disabled={!editable}
                        onChange={(event) =>
                          patchLine(line, {
                            ccaClassId: event.target.value === '' ? null : event.target.value,
                          })
                        }
                        className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
                      >
                        <option value="">Not chosen</option>
                        {lists.ccaClasses.map((cca) => (
                          <option key={cca.id} value={cca.id}>
                            {cca.classCode}, {cca.description}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                ) : null}

                {hasDivisions ? (
                  <Field
                    label="Division"
                    {...(values.divisionId === null
                      ? { hint: 'Set from the job, or the supplier, unless you say otherwise.' }
                      : {})}
                  >
                    {({ inputProps }) => (
                      <select
                        {...inputProps}
                        value={values.divisionId ?? ''}
                        disabled={!editable}
                        onChange={(event) =>
                          patchLine(line, {
                            divisionId: event.target.value === '' ? null : event.target.value,
                          })
                        }
                        className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
                      >
                        <option value="">Not assigned</option>
                        {lists.divisions.map((division) => (
                          <option key={division.id} value={division.id}>
                            {division.code}, {division.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                ) : null}

                <Field label="Job" required={settings.data?.requireJobLink ?? false}>
                  {({ inputProps }) => (
                    <select
                      {...inputProps}
                      value={values.jobId ?? ''}
                      disabled={!editable}
                      onChange={(event) =>
                        patchLine(line, {
                          jobId: event.target.value === '' ? null : event.target.value,
                        })
                      }
                      className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
                    >
                      <option value="">Not linked</option>
                      {lists.jobs.map((job) => (
                        <option key={job.id} value={job.id}>
                          {job.jobNumber === null ? job.title : `${job.jobNumber}, ${job.title}`}
                          {job.customerName === null ? '' : ` (${job.customerName})`}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
                {line.jobLinkedAutomatically &&
                values.jobId === line.jobId &&
                line.jobId !== null ? (
                  <p className="text-xs text-content-muted sm:col-span-2">
                    Linked to this job automatically: the person who spent it was on exactly one job
                    that day.
                  </p>
                ) : null}

                {lists.vehicles.length === 0 ? null : (
                  <Field
                    label="Vehicle"
                    hint="Fuel, repairs and insurance carry the vehicle's business share at year-end."
                  >
                    {({ inputProps }) => (
                      <select
                        {...inputProps}
                        value={values.vehicleId ?? ''}
                        disabled={!editable}
                        onChange={(event) =>
                          patchLine(line, {
                            vehicleId: event.target.value === '' ? null : event.target.value,
                          })
                        }
                        className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
                      >
                        <option value="">No vehicle</option>
                        {lists.vehicles.map((vehicle) => (
                          <option key={vehicle.id} value={vehicle.id}>
                            {vehicle.name}
                            {vehicle.plate === null ? '' : ` (${vehicle.plate})`}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                )}
              </div>
            );
          })}
        </CardBody>

        {editable ? (
          <CardFooter>
            <div className="flex w-full flex-col gap-3">
              {draft === null ? null : (
                <Field
                  label="Why the change"
                  hint="Optional, but this is what teaches the engine your chart of accounts."
                >
                  {({ inputProps }) => (
                    <Input
                      {...inputProps}
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      placeholder="Supplier bills refrigerant, not equipment"
                    />
                  )}
                </Field>
              )}
              <div className="flex items-center gap-2">
                <Button
                  id="record-save"
                  onClick={onSave}
                  disabled={draft === null}
                  isLoading={save.isPending}
                  title="Ctrl or Cmd + S"
                >
                  Save changes
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => confirm.mutate()}
                  isLoading={confirm.isPending}
                  disabled={draft !== null}
                  title={draft === null ? 'Ctrl or Cmd + Enter' : 'Save your changes first'}
                >
                  Confirm as accountant-ready
                </Button>
              </div>
            </div>
          </CardFooter>
        ) : null}
      </Card>

      {editable ? (
        <Card>
          <CardHeader title="Note" description="Attached to the record when you confirm it." />
          <CardBody>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={3}
              aria-label="Note"
              className="w-full rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 py-2 text-sm"
            />
          </CardBody>
        </Card>
      ) : null}

      {data.status !== 'confirmed' ? null : (
        <Card>
          <CardHeader
            title="Correct or void"
            description="A signed record is never edited. Correct it and a draft copy opens for you to fix and sign, with the original's postings reversed. Void it and it is reversed and stays on file, marked voided. Either way, say why: the reason stays on the record and in the Timeline."
          />
          <CardBody className="flex flex-col gap-3">
            <Field
              label="Why"
              hint="A few words. An accountant reading this in a year should understand."
            >
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  value={whyGone}
                  onChange={(event) => setWhyGone(event.target.value)}
                  placeholder="Coded to equipment; it was a repair part"
                />
              )}
            </Field>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => correctRecord.mutate()}
                isLoading={correctRecord.isPending}
                disabled={whyGone.trim().length < 5}
              >
                Correct this record
              </Button>
              <Button
                variant="danger"
                onClick={() => voidRecord.mutate()}
                isLoading={voidRecord.isPending}
                disabled={whyGone.trim().length < 5}
              >
                Void this record
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Where this came from"
          description="Every value the engine proposed is a lookup a person can retrace."
        />
        <CardBody className="grid gap-2 text-sm sm:grid-cols-2">
          <Provenance label="Approved by" value={data.provenance.approvedByName} />
          <Provenance label="Approved at" value={data.provenance.approvedAt} />
          <Provenance
            label="Corrections at review"
            value={String(data.provenance.correctionCount)}
          />
          <Provenance
            label="Supplier matched by"
            value={
              data.provenance.matchedVendorAliasId === null ? 'No alias matched' : 'A vendor alias'
            }
          />
          <Provenance label="Export state" value={data.exportState.replaceAll('_', ' ')} />
        </CardBody>
      </Card>

      <RelationshipsPanel
        organizationId={organizationId}
        entityType="transaction"
        entityId={recordId}
      />

      <ObjectTimeline
        organizationId={organizationId}
        resourceType="transaction"
        resourceId={recordId}
      />
    </div>
  );
}

function Amount({ label, value, currency }: { label: string; value: string; currency: string }) {
  return (
    <div>
      <p className="text-xs text-content-muted">{label}</p>
      <p className="tabular text-base font-medium text-content">{formatMinor(value, currency)}</p>
    </div>
  );
}

function Provenance({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <span className="text-content-muted">{label}: </span>
      <span className="text-content">{value ?? 'Not recorded'}</span>
    </div>
  );
}

/**
 * The way back for a misreading. Amounts are never retyped here, so the
 * books keep agreeing with the paper; instead the document returns to Review,
 * with every earlier correction filled in, and approving it builds a fresh
 * record. Drafts only: a signed record is voided, not re-read.
 */
function SendBackToReview({
  organizationId,
  recordId,
}: {
  organizationId: string;
  recordId: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () =>
      sendRecordBackToReview(
        organizationId,
        recordId,
        note.trim() === '' ? undefined : note.trim(),
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: bookkeepingKeys.all(organizationId) });
      void queryClient.invalidateQueries({ queryKey: ['review'] });
      if (result.reviewItemId !== null) {
        void navigate({ to: '/review/$itemId', params: { itemId: result.reviewItemId } });
      }
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'It could not be sent back.'),
  });

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-[var(--radius-control)] border border-border bg-surface px-4 py-3 text-sm">
      <span className="text-content-muted">
        Did the AI misread an amount, date or item on this receipt?
      </span>
      {open ? (
        <>
          <Input
            aria-label="What was misread (optional)"
            placeholder="What was misread (optional)"
            className="w-64"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <Button size="sm" onClick={() => send.mutate()} isLoading={send.isPending}>
            Send back to Review
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </>
      ) : (
        <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
          Correct the reading
        </Button>
      )}
      {error === null ? null : <Alert tone="danger">{error}</Alert>}
    </div>
  );
}
